/**
 * OptionChainTable.js
 *
 * High-performance option chain table using react-virtuoso's TableVirtuoso.
 * Handles 100+ strikes without layout thrashing.
 *
 * Layout:
 *   [CE columns] | [STRIKE — sticky center] | [PE columns]
 *
 * Visual states:
 *   • ITM CE rows    — subtle green tint on the CE cell group
 *   • ITM PE rows    — subtle green tint on the PE cell group
 *   • ATM row        — amber highlight band across full row
 *   • LTP flicker    — 600ms green (up) / red (down) flash on price change
 *   • Checkbox       — multi-select for Strategy Builder
 *
 * On mobile (< 600px):
 *   The table switches to a horizontally scrollable layout with the strike
 *   column position: sticky at left, ensuring it's always visible.
 */
import React, { useState, useCallback, memo } from 'react';
import {
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Checkbox, Skeleton, Box,
    Typography, Chip, useTheme, useMediaQuery,
} from '@mui/material';
import { alpha, keyframes } from '@mui/system';
import { TableVirtuoso } from 'react-virtuoso';

import useFlicker          from './useFlicker';
import useOptionChainStore from './useOptionChainStore';
import StrikeContextMenu   from './StrikeContextMenu';
import {
    DATA_COLUMNS_CE, DATA_COLUMNS_PE,
    GREEKS_COLUMNS_CE, GREEKS_COLUMNS_PE,
} from './optionChainUtils';

// ─── Keyframe animations ──────────────────────────────────────────────────────
const flashUp = keyframes`
  0%   { background-color: rgba(76, 175, 80, 0.35); }
  100% { background-color: transparent; }
`;
const flashDown = keyframes`
  0%   { background-color: rgba(244, 67, 54, 0.35); }
  100% { background-color: transparent; }
`;

// ─── Constants ────────────────────────────────────────────────────────────────
const STRIKE_COL_WIDTH = 90;
const CHECKBOX_WIDTH   = 36;

const VirtuosoScroller = React.forwardRef((props, ref) => (
    <TableContainer
        {...props}
        ref={ref}
        sx={{
            overflowX: 'auto',
            overflowY: 'auto',
        }}
    />
));

function buildHeatOverlay(theme, tone, level = 'soft') {
    const palette = theme.palette[tone];
    const opacity = level === 'strong' ? 0.16 : 0.10;

    return {
        boxShadow: `inset 0 0 0 9999px ${alpha(palette.main, opacity)}`,
        color: palette.dark,
        fontWeight: 600,
    };
}

function getMetricValue(row, colKey) {
    switch (colKey) {
    case 'oiChg':
        return row.CE?.oiChg;
    case 'peOiChg':
        return row.PE?.oiChg;
    case 'ceIV':
    case 'ceIV2':
        return row.CE?.iv;
    case 'peIV':
    case 'peIV2':
        return row.PE?.iv;
    case 'ceDelta':
        return row.CE?.delta;
    case 'peDelta':
        return row.PE?.delta;
    case 'ceTheta':
        return row.CE?.theta;
    case 'peTheta':
        return row.PE?.theta;
    default:
        return null;
    }
}

function getHeatOverlay(theme, colKey, row) {
    const value = getMetricValue(row, colKey);
    if (value == null || Number.isNaN(value)) {
        return null;
    }

    if (colKey === 'oiChg' || colKey === 'peOiChg') {
        if (value >= 10) return buildHeatOverlay(theme, 'success', 'strong');
        if (value >= 2) return buildHeatOverlay(theme, 'success');
        if (value <= -10) return buildHeatOverlay(theme, 'error', 'strong');
        if (value <= -2) return buildHeatOverlay(theme, 'error');
        return null;
    }

    if (colKey === 'ceIV' || colKey === 'peIV' || colKey === 'ceIV2' || colKey === 'peIV2') {
        if (value >= 0.30) return buildHeatOverlay(theme, 'error');
        if (value >= 0.20) return buildHeatOverlay(theme, 'warning');
        return null;
    }

    if (colKey === 'ceDelta' || colKey === 'peDelta') {
        const absDelta = Math.abs(value);
        if (absDelta >= 0.40 && absDelta <= 0.60) return buildHeatOverlay(theme, 'success');
        if ((absDelta >= 0.25 && absDelta < 0.40) || (absDelta > 0.60 && absDelta <= 0.75)) {
            return buildHeatOverlay(theme, 'warning');
        }
        return null;
    }

    if (colKey === 'ceTheta' || colKey === 'peTheta') {
        if (value <= -8) return buildHeatOverlay(theme, 'error', 'strong');
        if (value <= -3) return buildHeatOverlay(theme, 'warning');
        return null;
    }

    return null;
}

// ─── Flicker cell ─────────────────────────────────────────────────────────────
const FlickerCell = memo(({ value, formattedValue, align, width, sx }) => {
    const flicker = useFlicker(value, 600);

    return (
        <TableCell
            align={align}
            sx={{
                width,
                minWidth: width,
                py: 0.5,
                px: 1,
                fontSize: '0.78rem',
                fontWeight: 500,
                fontFamily: '"Roboto Mono", monospace',
                animation: flicker === 'up'
                    ? `${flashUp} 600ms ease-out`
                    : flicker === 'down'
                    ? `${flashDown} 600ms ease-out`
                    : 'none',
                color: flicker === 'up'
                    ? 'success.main'
                    : flicker === 'down'
                    ? 'error.main'
                    : 'text.primary',
                transition: 'color 200ms',
                ...sx,
            }}
        >
            {formattedValue}
        </TableCell>
    );
});

// ─── Generic data cell ────────────────────────────────────────────────────────
const DataCell = memo(({ formattedValue, align, width, sx }) => (
    <TableCell
        align={align}
        sx={{
            width,
            minWidth: width,
            py: 0.5,
            px: 1,
            fontSize: '0.78rem',
            fontFamily: '"Roboto Mono", monospace',
            color: 'text.secondary',
            ...sx,
        }}
    >
        {formattedValue}
    </TableCell>
));

// ─── Row component ────────────────────────────────────────────────────────────
const OptionChainRow = memo(({ row, ceCols, peCols, onStrikeClick, isMobile }) => {
    const theme    = useTheme();
    const { toggleLeg, isSelected } = useOptionChainStore();

    const ceSelected = isSelected(row.strike, 'CE');
    const peSelected = isSelected(row.strike, 'PE');

    // Row-level background — ATM gets amber band
    const rowBg = row.isAtm
        ? alpha(theme.palette.warning.main, 0.10)
        : 'transparent';

    // Cell group backgrounds — ITM cells get green tint
    const ceGroupBg = row.ceItm
        ? alpha(theme.palette.success.main, 0.06)
        : 'transparent';
    const peGroupBg = row.peItm
        ? alpha(theme.palette.success.main, 0.06)
        : 'transparent';

    const ceCellBg = ceGroupBg !== 'transparent' ? ceGroupBg : rowBg;
    const peCellBg = peGroupBg !== 'transparent' ? peGroupBg : rowBg;

    return (
        <>
            {/* CE checkbox */}
            <TableCell padding='checkbox' sx={{ width: CHECKBOX_WIDTH, bgcolor: ceCellBg }}>
                <Checkbox
                    size='small'
                    checked={ceSelected}
                    onChange={() => toggleLeg(row.strike, 'CE')}
                    sx={{ p: 0.5, color: 'primary.light' }}
                />
            </TableCell>

            {/* CE data cells */}
            {ceCols.map((col) =>
                col.isLTP ? (
                    <FlickerCell
                        key={col.key}
                        value={row.CE?.ltp}
                        formattedValue={col.format(row)}
                        align={col.align}
                        width={col.width}
                        sx={{ bgcolor: ceCellBg }}
                    />
                ) : (
                    <DataCell
                        key={col.key}
                        formattedValue={col.format(row)}
                        align={col.align}
                        width={col.width}
                        sx={{ bgcolor: ceCellBg, ...getHeatOverlay(theme, col.key, row) }}
                    />
                )
            )}

            {/* Strike — sticky center */}
            <TableCell
                align='center'
                onClick={(e) => onStrikeClick(e, row.strike)}
                sx={{
                    width:        STRIKE_COL_WIDTH,
                    minWidth:     STRIKE_COL_WIDTH,
                    position:     'sticky',
                    // On mobile sticky left; on desktop we rely on overflowX scroll
                    left:         isMobile ? CHECKBOX_WIDTH : 'auto',
                    zIndex:       1,
                    bgcolor:      row.isAtm
                        ? alpha(theme.palette.warning.main, 0.18)
                        : theme.palette.background.paper,
                    fontWeight:   row.isAtm ? 800 : 600,
                    fontSize:     row.isAtm ? '0.85rem' : '0.80rem',
                    fontFamily:   '"Roboto Mono", monospace',
                    color:        row.isAtm ? 'warning.main' : 'text.primary',
                    cursor:       'pointer',
                    userSelect:   'none',
                    borderLeft:   `1px solid ${theme.palette.divider}`,
                    borderRight:  `1px solid ${theme.palette.divider}`,
                    py: 0.5,
                    '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.12) },
                }}
            >
                {row.isAtm && (
                    <Typography
                        component='span'
                        sx={{
                            display: 'block',
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            color: 'warning.main',
                            lineHeight: 1,
                            mb: 0.2,
                            letterSpacing: 0.5,
                        }}
                    >
                        ATM
                    </Typography>
                )}
                {row.strike.toLocaleString('en-IN')}
            </TableCell>

            {/* PE data cells */}
            {peCols.map((col) =>
                col.isLTP ? (
                    <FlickerCell
                        key={col.key}
                        value={row.PE?.ltp}
                        formattedValue={col.format(row)}
                        align={col.align}
                        width={col.width}
                        sx={{ bgcolor: peCellBg }}
                    />
                ) : (
                    <DataCell
                        key={col.key}
                        formattedValue={col.format(row)}
                        align={col.align}
                        width={col.width}
                        sx={{ bgcolor: peCellBg, ...getHeatOverlay(theme, col.key, row) }}
                    />
                )
            )}

            {/* PE checkbox */}
            <TableCell padding='checkbox' sx={{ width: CHECKBOX_WIDTH, bgcolor: peCellBg }}>
                <Checkbox
                    size='small'
                    checked={peSelected}
                    onChange={() => toggleLeg(row.strike, 'PE')}
                    sx={{ p: 0.5, color: 'secondary.light' }}
                />
            </TableCell>
        </>
    );
});

// ─── Header ───────────────────────────────────────────────────────────────────
function HeaderContent({ ceCols, peCols, isMobile }) {
    const theme = useTheme();

    const headerCellSx = {
        py: 0.75,
        px: 1,
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: 0.4,
        color: 'text.secondary',
        bgcolor: (t) => alpha(t.palette.background.default, 0.9),
        borderBottom: `2px solid ${theme.palette.divider}`,
        whiteSpace: 'nowrap',
    };

    return (
        <>
            {/* Group header row */}
            <TableRow>
                <TableCell
                    colSpan={ceCols.length + 1}
                    align='center'
                    sx={{
                        ...headerCellSx,
                        color: 'primary.main',
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                        borderBottom: 'none',
                        py: 0.4,
                        fontSize: '0.68rem',
                        letterSpacing: 1,
                        fontWeight: 800,
                    }}
                >
                    CALLS
                </TableCell>
                <TableCell
                    align='center'
                    sx={{
                        ...headerCellSx,
                        position:  'sticky',
                        left:      isMobile ? CHECKBOX_WIDTH : 'auto',
                        zIndex:    2,
                        bgcolor:   theme.palette.background.paper,
                        borderBottom: 'none',
                    }}
                />
                <TableCell
                    colSpan={peCols.length + 1}
                    align='center'
                    sx={{
                        ...headerCellSx,
                        color: 'secondary.main',
                        bgcolor: (t) => alpha(t.palette.secondary.main, 0.06),
                        borderBottom: 'none',
                        py: 0.4,
                        fontSize: '0.68rem',
                        letterSpacing: 1,
                        fontWeight: 800,
                    }}
                >
                    PUTS
                </TableCell>
            </TableRow>

            {/* Column header row */}
            <TableRow>
                <TableCell sx={{ ...headerCellSx, width: CHECKBOX_WIDTH }} />
                {ceCols.map((col) => (
                    <TableCell key={col.key} align={col.align} sx={{ ...headerCellSx, width: col.width }}>
                        {col.label}
                    </TableCell>
                ))}
                <TableCell
                    align='center'
                    sx={{
                        ...headerCellSx,
                        width:     STRIKE_COL_WIDTH,
                        position:  'sticky',
                        left:      isMobile ? CHECKBOX_WIDTH : 'auto',
                        zIndex:    2,
                        bgcolor:   theme.palette.background.paper,
                        color:     'text.primary',
                        fontWeight: 800,
                    }}
                >
                    STRIKE
                </TableCell>
                {peCols.map((col) => (
                    <TableCell key={col.key} align={col.align} sx={{ ...headerCellSx, width: col.width }}>
                        {col.label}
                    </TableCell>
                ))}
                <TableCell sx={{ ...headerCellSx, width: CHECKBOX_WIDTH }} />
            </TableRow>
        </>
    );
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────
function TableSkeleton({ rows = 18 }) {
    return (
        <Box sx={{ px: 1 }}>
            {Array.from({ length: rows }).map((_, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, py: 0.5 }}>
                    <Skeleton variant='rectangular' width='45%' height={32} sx={{ borderRadius: 1 }} />
                    <Skeleton variant='rectangular' width='10%' height={32} sx={{ borderRadius: 1 }} />
                    <Skeleton variant='rectangular' width='45%' height={32} sx={{ borderRadius: 1 }} />
                </Box>
            ))}
        </Box>
    );
}

// ─── Main table ───────────────────────────────────────────────────────────────
export default function OptionChainTable({ chain, viewMode, tableHeight, isLoading }) {
    const isMobile = useMediaQuery('(max-width:600px)');

    const [contextMenu, setContextMenu] = useState({ anchorEl: null, strike: null });

    const ceCols = viewMode === 'greeks' ? GREEKS_COLUMNS_CE : DATA_COLUMNS_CE;
    const peCols = viewMode === 'greeks' ? GREEKS_COLUMNS_PE : DATA_COLUMNS_PE;
    const tableMinWidth =
        CHECKBOX_WIDTH * 2
        + STRIKE_COL_WIDTH
        + ceCols.reduce((sum, col) => sum + col.width, 0)
        + peCols.reduce((sum, col) => sum + col.width, 0);

    const handleStrikeClick = useCallback((e, strike) => {
        setContextMenu({ anchorEl: e.currentTarget, strike });
    }, []);

    const handleMenuClose = useCallback(() => {
        setContextMenu({ anchorEl: null, strike: null });
    }, []);

    if (isLoading) return <TableSkeleton />;
    if (!chain?.length) {
        return (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                <Typography variant='body2'>
                    No chain data available. Is the Greeks engine running?
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <TableVirtuoso
                data={chain}
                style={{ height: tableHeight ?? 'calc(100vh - 280px)' }}
                fixedHeaderContent={() => (
                    <HeaderContent ceCols={ceCols} peCols={peCols} isMobile={isMobile} />
                )}
                components={{
                    Scroller: VirtuosoScroller,
                    // Plug into MUI's Table primitives so MUI styling applies
                    Table: (props) => (
                        <Table
                            {...props}
                            size='small'
                            stickyHeader
                            sx={{
                                tableLayout: 'fixed',
                                minWidth: tableMinWidth,
                            }}
                        />
                    ),
                    TableHead,
                    TableBody: React.forwardRef((props, ref) => (
                        <TableBody {...props} ref={ref} />
                    )),
                    TableRow: (props) => <TableRow {...props} />,
                }}
                itemContent={(_, row) => (
                    // TableVirtuoso renders the <tr>; itemContent should return only cells.
                    <OptionChainRow
                        row={row}
                        ceCols={ceCols}
                        peCols={peCols}
                        onStrikeClick={handleStrikeClick}
                        isMobile={isMobile}
                    />
                )}
                overscan={10}
            />

            <StrikeContextMenu
                anchorEl={contextMenu.anchorEl}
                strike={contextMenu.strike}
                onClose={handleMenuClose}
            />
        </>
    );
}
